import { AlertTriangle, CheckCircle2, FileBarChart, RefreshCw, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generateReport, loadReports, type PostStreamReport } from './reports-api';
import './reports.css';

function when(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB') : '—';
}

function number(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('en-GB') : '0';
}

function detailText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return String(value ?? 'Recorded event');
  const item = value as Record<string, unknown>;
  for (const key of ['message', 'label', 'subsystem', 'detail', 'reason', 'state']) {
    if (typeof item[key] === 'string' && item[key]) return String(item[key]);
  }
  try { return JSON.stringify(value); } catch { return 'Recorded event'; }
}

export function ReportsPage({ role }: { role: 'owner' | 'operator' }) {
  const [reports, setReports] = useState<PostStreamReport[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      const next = await loadReports();
      setReports(next);
      setSelectedId((current) => current && next.some((item) => item.id === current) ? current : next[0]?.id ?? '');
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Reports could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);
  const selected = useMemo(() => reports.find((item) => item.id === selectedId) ?? null, [reports, selectedId]);

  async function createReport() {
    setBusy(true);
    setMessage('');
    try {
      await generateReport();
      await refresh();
      setMessage('Post-stream report generated from the latest completed stream session.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Report generation failed.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="ffs-next-loading">Loading Reports…</div>;

  return (
    <div className="ffs-workspace-page ffs-reports-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}
      <section className="ffs-workspace-hero">
        <div><small>AFTER THE STREAM</small><h2>Reports</h2><p>Review meaningful failures, recoveries and unresolved incidents without trawling through raw logs.</p></div>
        <div className="ffs-reports-hero-actions"><span className="ffs-status">{role === 'owner' ? 'OWNER' : 'LEAD MODERATOR'}</span><button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void refresh()}><RefreshCw size={14} /> Refresh</button><button type="button" className="ffs-button primary" disabled={busy} onClick={() => void createReport()}><FileBarChart size={14} /> Generate Latest Report</button></div>
      </section>

      <section className="ffs-reports-grid">
        <article className="ffs-workspace-card ffs-reports-list-card">
          <header><div><small>{reports.length} REPORTS</small><h3>Stream history</h3></div><FileBarChart size={18} /></header>
          <div className="ffs-reports-list">{reports.length === 0 ? <div className="ffs-empty-state">No post-stream reports have been generated yet.</div> : reports.map((report) => <button type="button" key={report.id} className={report.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(report.id)}><div><strong>{when(report.created_at)}</strong><span>{report.summary}</span></div><small>{report.status}</small></button>)}</div>
        </article>

        <article className="ffs-workspace-card ffs-report-detail-card">
          {!selected ? <div className="ffs-empty-state">Select a report to inspect it.</div> : <>
            <header><div><small>POST-STREAM REPORT</small><h3>{when(selected.created_at)}</h3></div><span>{selected.status}</span></header>
            <div className="ffs-report-summary"><strong>{selected.summary}</strong><small>Stream session {selected.stream_session_id}</small></div>
            <div className="ffs-report-totals">
              <ReportMetric label="Unresolved" value={number(selected.totals?.unresolvedIncidents)} bad={Number(selected.totals?.unresolvedIncidents ?? 0) > 0} />
              <ReportMetric label="Recovered" value={number(selected.totals?.automaticRecoveries)} />
              <ReportMetric label="Failed alerts" value={number(selected.totals?.failedAlerts)} bad={Number(selected.totals?.failedAlerts ?? 0) > 0} />
              <ReportMetric label="Failed audio" value={number(selected.totals?.failedAudio)} bad={Number(selected.totals?.failedAudio ?? 0) > 0} />
            </div>
            <ReportSection title="Unresolved incidents" items={selected.unresolved_incidents ?? []} icon="warning" empty="Nothing remained unresolved at report generation." />
            <ReportSection title="Automatic recoveries" items={selected.automatic_recoveries ?? []} icon="recovered" empty="No automatic recovery events were recorded." />
            <ReportSection title="Significant failures" items={selected.significant_failures ?? []} icon="warning" empty="No significant failures were recorded." />
          </>}
        </article>
      </section>
    </div>
  );
}

function ReportMetric({ label, value, bad = false }: { label: string; value: string; bad?: boolean }) {
  return <div className={bad ? 'bad' : ''}><span>{label}</span><strong>{value}</strong></div>;
}

function ReportSection({ title, items, icon, empty }: { title: string; items: unknown[]; icon: 'warning' | 'recovered'; empty: string }) {
  return <section className="ffs-report-section"><h4>{icon === 'warning' ? <AlertTriangle size={15} /> : <RotateCcw size={15} />}{title}<span>{items.length}</span></h4>{items.length === 0 ? <div className="ffs-report-empty"><CheckCircle2 size={14} /> {empty}</div> : <div className="ffs-report-event-list">{items.map((item, index) => <div key={index}>{detailText(item)}</div>)}</div>}</section>;
}
